import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../../layouts/dashboard';
import LogoOnlyLayout from '../../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../../components/LoadingScreen';
import {
  ProductApprovalOperaterUsersRoutes,
  TagRoutes,
  PostsRoutes,
  CategoriesRoutes,
  PagesRoutes,
  ProductsRoutes,
  RetailersRoutes,
  DistributorsRoutes,
  PosUsersRoutes,
  ProductMatchIndex,
  EndUsersRoutes,
  PartnerUsersRoutes,
  TransactionsRoutes,
  BrandsRoutes,
  LoginRoot,
  AdminUsersRoutes,
  BannersRoutes,
  ContentEditorUsersRoutes,
  UnderwriterUsersRoutes,
  PlatformAdminUsersRoutes,
  InsuranceAdminUsersRoutes,
  InsuranceAgentUsersRoutes,
  ForbiddenRoot,
  HomePagesRoutes,
  SpecificationAdminRoutes,
  SpecificationRootes,
  RetailerTransactionsRootes,
  DistributorTransactionsRootes,
  UserRoutes,
  CodeBookIndex,
  ContractorsRoutes,
  ReportsRoutes,
  AgentsRoutes,
  EnterProductNames,
  FiscalBillsRoutes,
  FiscalItemClaimsRoutes,
  BlackListRoutes,
  WhiteListIndex,
  PushNotificationsRoutes,
  InvoicesRoutes,
} from './Routes';
import ForbiddenPage from 'src/theme-code/pages/ForbiddenPage';
import { useUser } from '../features/Auth/applications/hooks/useUser';
import RetailersPage from '../features/Loyalty/partners/retailerPages/Index/RetailersPage';
import { SendNotificationToAllUsersPage } from '../features/PushNotifications/pages';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { getHomeRoute } = useUser();
  const homeRoute = getHomeRoute();
  return useRoutes([
    {
      path: '/',
      element: <Navigate to={LoginRoot} replace />,
    },
    {
      path: LoginRoot,
      element: <LoginPage />,
    },
    {
      path: ForbiddenRoot,
      element: <ForbiddenPage />,
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to={homeRoute} replace />, index: true },
        {
          path: UserRoutes.commonProfile,
          element: <UserProfilePage />,
        },
        {
          path: HomePagesRoutes.contentEditorHomePage,
          element: <ContentEditorHomePage />,
        },
        {
          path: HomePagesRoutes.distributorHomePage,
          element: <DistributorHomePage />,
        },
        {
          path: HomePagesRoutes.platformAdminHomePage,
          element: <PlatformAdminHomePage />,
        },
        {
          path: HomePagesRoutes.retailerHomePage,
          element: <RetailerHomePage />,
        },
        {
          path: HomePagesRoutes.productApprovalOperaterHomePage,
          element: <ProductApprovalOperaterHomePage />,
        },
        { path: 'one', element: <PageOne /> },
        { path: 'two', element: <PageTwo /> },
        { path: 'three', element: <PageThree /> },
        {
          path: 'Korisnici',
          children: [
            { element: <Navigate to="/dashboard/user/four" replace />, index: true },
            { path: 'Partneri', element: <PageFour /> },
            { path: 'Osiguranici', element: <PageFive /> },
            { path: 'Administratori osiguranja', element: <PageSix /> },
            { path: 'Savetnici', element: <PageSix /> },
          ],
        },
        {
          path: TransactionsRoutes.root,
          children: [
            { path: TransactionsRoutes.index, element: <TransactionsPage /> },
            {
              path: TransactionsRoutes.details + '/:id',
              element: <TransactionDetails />,
            },
          ],
        },
        {
          path: RetailerTransactionsRootes.root,
          children: [
            { path: RetailerTransactionsRootes.index, element: <RetailerTransactionsPage /> },
            {
              path: RetailerTransactionsRootes.details + '/:id',
              element: <RetailerTransactionsDetailsPage />,
            },
          ],
        },
        {
          path: DistributorTransactionsRootes.root,
          children: [
            { path: DistributorTransactionsRootes.index, element: <DistributorTransactionsPage /> },
            {
              path: DistributorTransactionsRootes.details + '/:id',
              element: <DistributorTransactionsDetailsPage />,
            },
          ],
        },
        {
          path: SpecificationAdminRoutes.root,
          children: [{ path: SpecificationAdminRoutes.index, element: <SpecificationAdminPage /> }],
        },
        {
          path: SpecificationRootes.root,
          children: [{ path: SpecificationRootes.index, element: <SpecificationIndexPage /> }],
        },
        {
          path: FiscalBillsRoutes.root,
          children: [
            { path: FiscalBillsRoutes.index, element: <FiscalBillsPageWithPfrSearch /> },
            { path: FiscalBillsRoutes.details, element: <FiscalBillDetailsPage /> },
          ],
        },
        {
          path: FiscalItemClaimsRoutes.root,
          children: [
            { path: FiscalItemClaimsRoutes.index, element: <FiscalItemClaimsPage /> },
            { path: FiscalItemClaimsRoutes.details, element: <FiscalItemClaimDetailsPage /> },
          ],
        },
        {
          path: PostsRoutes.root,
          children: [
            { element: <Navigate to={PostsRoutes.index} replace />, index: true },
            { path: PostsRoutes.index, element: <PostsPage /> },
            { path: PostsRoutes.create, element: <CreatePostPage /> },
            { path: PostsRoutes.details + '/:id', element: <DetailsPostPage /> },
            { path: PostsRoutes.update + '/:id', element: <UpdatePostPage /> },
          ],
        },
        {
          path: ProductsRoutes.root,
          children: [
            { element: <Navigate to={ProductsRoutes.index} replace />, index: true },
            { path: ProductsRoutes.index, element: <ProductsPage /> },
            { path: ProductsRoutes.create, element: <CreateProductPage /> },
            { path: ProductsRoutes.details, element: <DetailsProductPage /> },
            { path: ProductsRoutes.update, element: <UpdateProductPage /> },
            { path: ProductsRoutes.addProductImage, element: <AddProductImagePage /> },
          ],
        },
        {
          path: DistributorsRoutes.root,
          children: [
            { element: <Navigate to={DistributorsRoutes.index} replace />, index: true },
            { path: DistributorsRoutes.index, element: <DistributorsPage /> },
            { path: DistributorsRoutes.create, element: <CreateDistributorPage /> },
            { path: DistributorsRoutes.details + '/:id', element: <DetailsDistributorPage /> },
            { path: DistributorsRoutes.update + '/:id', element: <UpdateDistributorPage /> },
            { path: DistributorsRoutes.profile + '/:id', element: <DistributorProfilePage /> },
            { path: DistributorsRoutes.brandIndex, element: <BrandsPage /> },
            {
              path: DistributorsRoutes.brandDetails + '/:id',
              element: <DistributorDetailsBrandPage />,
            },
            {
              path: DistributorsRoutes.productsIndex + '/:id',
              element: <DistributerProductsPage />,
            },
            {
              path: DistributorsRoutes.productDetails + '/:brandId/:id',
              element: <DistributerDetailsProductPage />,
            },
          ],
        },
        {
          path: BrandsRoutes.root,
          children: [
            { element: <Navigate to={BrandsRoutes.index} replace />, index: true },
            { path: BrandsRoutes.index, element: <BrandsByDistributorIdPage /> },
            { path: BrandsRoutes.create, element: <CreateBrandPage /> },
            { path: BrandsRoutes.details, element: <DetailsBrandPage /> },
            { path: BrandsRoutes.update, element: <UpdateBrandPage /> },
          ],
        },
        {
          path: RetailersRoutes.root,
          children: [
            { element: <Navigate to={RetailersRoutes.index} replace />, index: true },
            { path: RetailersRoutes.index, element: <RetailersPage /> },
            { path: RetailersRoutes.create, element: <CreateRetailerPage /> },
            { path: RetailersRoutes.details + '/:id', element: <DetailsRetailerPage /> },
            { path: RetailersRoutes.update + '/:id', element: <UpdateRetailerPage /> },
            { path: RetailersRoutes.profile + '/:id', element: <RetailerProfilePage /> },
            { path: RetailersRoutes.posUserIndex, element: <RetailerPosUsersPage /> },
            {
              path: RetailersRoutes.posUserDetails + '/:id',
              element: <RetailerDetailsPosUserPage />,
            },
          ],
        },
        {
          path: PosUsersRoutes.root,
          children: [
            { element: <Navigate to={PosUsersRoutes.index} replace />, index: true },
            { path: PosUsersRoutes.index, element: <PosUsersByRetailerIdPage /> },
            { path: PosUsersRoutes.create, element: <CreatePosUserPage /> },
            { path: PosUsersRoutes.details, element: <DetailsPosUserPage /> },
            {
              path: PosUsersRoutes.addSimpleCashbackSpecification,
              element: <CreateSimpleCashbackSpecificationPage />,
            },
            {
              path: PosUsersRoutes.addCascadingCashbackSpecification,
              element: <CreateCascadingCashbackSpecificationPage />,
            },
            {
              path: PosUsersRoutes.createCustomTransaction,
              element: <CreateCustomTransactionPage />,
            },
          ],
        },
        {
          path: ContractorsRoutes.root,
          children: [
            { element: <Navigate to={ContractorsRoutes.indexRisk} replace />, index: true },
            { path: ContractorsRoutes.details, element: <DetailsContractorPage /> },
            { path: ContractorsRoutes.detailsRisk, element: <DetailsRiskContractorPage /> },
            { path: ContractorsRoutes.index, element: <IndexContractorsInProcessPage /> },
            { path: ContractorsRoutes.indexRisk, element: <IndexRiskContractorPage /> },
          ],
        },
        {
          path: EndUsersRoutes.root,
          children: [
            { element: <Navigate to={EndUsersRoutes.create} replace />, index: true },
            { path: EndUsersRoutes.details, element: <DetailsEndUserPage /> },
            { path: EndUsersRoutes.index, element: <EndUsersPage /> },
            { path: EndUsersRoutes.transactionDetails, element: <TransactionDetailsPage /> },
            {
              path: EndUsersRoutes.fiscalBillDetails,
              element: <FiscalBillDetailsPageForEndUsers />,
            },
          ],
        },
        {
          path: AdminUsersRoutes.root,
          children: [
            { element: <Navigate to={EndUsersRoutes.create} replace />, index: true },
            { path: AdminUsersRoutes.details + '/:id', element: <DetailsAdminUserPage /> },
            { path: AdminUsersRoutes.index, element: <AdminUsersPage /> },
          ],
        },
        {
          path: ProductApprovalOperaterUsersRoutes.root,
          children: [
            {
              element: <Navigate to={ProductApprovalOperaterUsersRoutes.create} replace />,
              index: true,
            },
            {
              path: ProductApprovalOperaterUsersRoutes.create,
              element: <CreateProductApprovalOperaterUserPage />,
            },
          ],
        },
        {
          path: ContentEditorUsersRoutes.root,
          children: [
            { element: <Navigate to={ContentEditorUsersRoutes.create} replace />, index: true },
            {
              path: ContentEditorUsersRoutes.create,
              element: <CreateContentEditorUserPage />,
            },
          ],
        },
        {
          path: UnderwriterUsersRoutes.root,
          children: [
            { element: <Navigate to={UnderwriterUsersRoutes.create} replace />, index: true },
            {
              path: UnderwriterUsersRoutes.create,
              element: <CreateUnderwriterUserPage />,
            },
          ],
        },
        {
          path: PushNotificationsRoutes.root,
          children: [
            {
              element: <Navigate to={PushNotificationsRoutes.sendNotification} replace />,
              index: true,
            },
            {
              path: PushNotificationsRoutes.sendNotification,
              element: <SendNotificationToAllUsersPage />,
            },
          ],
        },
        {
          path: PlatformAdminUsersRoutes.root,
          children: [
            { element: <Navigate to={PlatformAdminUsersRoutes.create} replace />, index: true },
            {
              path: PlatformAdminUsersRoutes.create,
              element: <CreatePlatformAdminUserPage />,
            },
          ],
        },
        {
          path: InsuranceAdminUsersRoutes.root,
          children: [
            { element: <Navigate to={InsuranceAdminUsersRoutes.create} replace />, index: true },
            {
              path: InsuranceAdminUsersRoutes.create,
              element: <CreateInsuranceAdminUserPage />,
            },
          ],
        },
        {
          path: InsuranceAgentUsersRoutes.root,
          children: [
            { element: <Navigate to={InsuranceAgentUsersRoutes.create} replace />, index: true },
            {
              path: InsuranceAgentUsersRoutes.create,
              element: <CreateInsuranceAgentUserPage />,
            },
          ],
        },
        {
          path: AgentsRoutes.root,
          children: [
            { element: <Navigate to={AgentsRoutes.indexExternal} replace />, index: true },
            {
              path: AgentsRoutes.indexInternal,
              element: <InternalAgentsPage />,
            },
            {
              path: AgentsRoutes.indexExternal,
              element: <ExternalAgentsPage />,
            },
          ],
        },
        {
          path: PartnerUsersRoutes.root,
          children: [
            { element: <Navigate to={PartnerUsersRoutes.index} replace />, index: true },
            { path: PartnerUsersRoutes.index, element: <PartnerUsersByPartnerIdPage /> },
            { path: PartnerUsersRoutes.create, element: <CreatePartnerUserPage /> },
            { path: PartnerUsersRoutes.details, element: <DetailsPartnerUserPage /> },
          ],
        },
        {
          path: CategoriesRoutes.root,
          children: [
            { element: <Navigate to={CategoriesRoutes.index} replace />, index: true },
            { path: CategoriesRoutes.index, element: <CategoriesPage /> },
            { path: CategoriesRoutes.create, element: <CreateCategoryPage /> },
            { path: CategoriesRoutes.details + '/:id', element: <DetailsCategoryPage /> },
            { path: CategoriesRoutes.update + '/:id', element: <UpdateCategoryPage /> },
          ],
        },
        {
          path: TagRoutes.root,
          children: [
            { element: <Navigate to={TagRoutes.index} replace />, index: true },
            { path: TagRoutes.index, element: <TagsPage /> },
            { path: TagRoutes.create, element: <CreateTagPage /> },
            { path: TagRoutes.details + '/:id', element: <DetailsTagPage /> },
            { path: TagRoutes.update + '/:id', element: <UpdateTagPage /> },
          ],
        },
        {
          path: PagesRoutes.root,
          children: [
            { element: <Navigate to={PagesRoutes.index} replace />, index: true },
            { path: PagesRoutes.index, element: <PagesPage /> },
            { path: PagesRoutes.create, element: <CreatePagePage /> },
            { path: PagesRoutes.details + '/:id', element: <DetailsPagePage /> },
            { path: PagesRoutes.update + '/:id', element: <UpdatePagePage /> },
          ],
        },
        {
          path: BannersRoutes.root,
          children: [
            { element: <Navigate to={BannersRoutes.index} replace />, index: true },
            { path: BannersRoutes.index, element: <BannersPage /> },
            { path: BannersRoutes.create, element: <CreateBannerPage /> },
            { path: BannersRoutes.details + '/:id', element: <DetailsBannerPage /> },
            { path: BannersRoutes.update + '/:id', element: <UpdateBannerPage /> },
          ],
        },
        { path: ProductMatchIndex, element: <ProductMatchPage /> },
        { path: EnterProductNames, element: <ProductNamesEntryPage /> },
        { path: CodeBookIndex, element: <CodeBookSettingsPage /> },
        {
          path: BlackListRoutes.root,
          children: [
            { element: <Navigate to={BlackListRoutes.index} replace />, index: true },
            { path: BlackListRoutes.index, element: <BlackListPage /> },
          ],
        },
        { path: WhiteListIndex, element: <ProductMatchesPage /> },
        {
          path: InvoicesRoutes.root,
          children: [
            { element: <Navigate to={InvoicesRoutes.indexWithErrors} replace />, index: true },
            { path: InvoicesRoutes.indexWithErrors, element: <InvoicesWithErrorsPage /> },
          ],
        },
        {
          path: ReportsRoutes.root,
          children: [
            { element: <Navigate to={ReportsRoutes.indexDailyPayments} replace />, index: true },
            { path: ReportsRoutes.indexDailyPayments, element: <DailyPaymentsPage /> },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// Dashboard
const PageOne = Loadable(lazy(() => import('../../theme-code/pages/PageOne')));
const PageTwo = Loadable(lazy(() => import('../../theme-code/pages/PageTwo')));
const PageThree = Loadable(lazy(() => import('../../theme-code/pages/PageThree')));
const PageFour = Loadable(lazy(() => import('../../theme-code/pages/PageFour')));
const PageFive = Loadable(lazy(() => import('../../theme-code/pages/PageFive')));
const PageSix = Loadable(lazy(() => import('../../theme-code/pages/PageSix')));
const NotFound = Loadable(lazy(() => import('../../theme-code/pages/Page404')));
//LoginPage
const LoginPage = Loadable(lazy(() => import('../features/Auth/components/LoginPage')));
const UserProfilePage = Loadable(
  lazy(() => import('../features/Auth/pages/Profile/UserProfilePage'))
);
//posts
const PostsPage = Loadable(lazy(() => import('../features/CMS/posts/pages/Index/PostsPage')));
const CreatePostPage = Loadable(
  lazy(() => import('../features/CMS/posts/pages/Create/CreatePostPage'))
);
const UpdatePostPage = Loadable(
  lazy(() => import('../features/CMS/posts/pages/Update/UpdatePostPage'))
);
const DetailsPostPage = Loadable(
  lazy(() => import('../features/CMS/posts/pages/Details/DetailsPostPage'))
);
//categories
const CategoriesPage = Loadable(
  lazy(() => import('../features/CMS/categories/pages/Index/CategoriesPage'))
);
const CreateCategoryPage = Loadable(
  lazy(() => import('../features/CMS/categories/pages/Create/CreateCategoryPage'))
);
const UpdateCategoryPage = Loadable(
  lazy(() => import('../features/CMS/categories/pages/Update/UpdateCategoryPage'))
);
const DetailsCategoryPage = Loadable(
  lazy(() => import('../features/CMS/categories/pages/Details/DetailsCategoryPage'))
);
//tags
const TagsPage = Loadable(lazy(() => import('../features/CMS/tags/pages/Index/TagsPage')));
const CreateTagPage = Loadable(
  lazy(() => import('../features/CMS/tags/pages/Create/CreateTagPage'))
);
const UpdateTagPage = Loadable(
  lazy(() => import('../features/CMS/tags/pages/Update/UpdateTagPage'))
);
const DetailsTagPage = Loadable(
  lazy(() => import('../features/CMS/tags/pages/Details/DetailsTagPage'))
);
//home pages
const ContentEditorHomePage = Loadable(lazy(() => import('../pages/ContentEditorHomePage')));
const DistributorHomePage = Loadable(
  lazy(() => import('../pages/DistributorPartnerAdminHomePage'))
);
const PlatformAdminHomePage = Loadable(lazy(() => import('../pages/PlatformAdminHomePage')));
const RetailerHomePage = Loadable(lazy(() => import('../pages/RetailerPartnerAdminHomePage')));
const ProductApprovalOperaterHomePage = Loadable(
  lazy(() => import('../pages/ProductApprovalOperatorHomePage'))
);

//products
const ProductsPage = Loadable(
  lazy(() => import('../features/Loyalty/products/components/ProductsPage'))
);
const CreateProductPage = Loadable(
  lazy(() => import('../features/Loyalty/products/components/CreateProductPage'))
);
const DetailsProductPage = Loadable(
  lazy(() => import('../features/Loyalty/products/components/details/DetailsProductPage'))
);
const UpdateProductPage = Loadable(
  lazy(() => import('../features/Loyalty/products/components/UpdateProductPage'))
);
const AddProductImagePage = Loadable(
  lazy(() => import('../features/Loyalty/products/components/addProductImage/AddProductImagePage'))
);
//distributors
const DistributorsPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/Index/DistributorsPage'))
);
const CreateDistributorPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/Create/CreateDistributorPage'))
);
const DetailsDistributorPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/Details/DetailsDistributorPage'))
);
const UpdateDistributorPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/Update/UpdateDistributorPage'))
);
//brands
const CreateBrandPage = Loadable(
  lazy(() => import('../features/Loyalty/brands/components/CreateBrandPage'))
);
const DetailsBrandPage = Loadable(
  lazy(() => import('../features/Loyalty/brands/components/DetailsBrandPage'))
);
const UpdateBrandPage = Loadable(
  lazy(() => import('../features/Loyalty/brands/components/UpdateBrandPage'))
);
const BrandsByDistributorIdPage = Loadable(
  lazy(() => import('../features/Loyalty/brands/components/BrandsByDistributorIdPage'))
);
const DistributerDetailsProductPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/partners/distributorPages/ProductDetails/DistributerDetailsProductPage'
      )
  )
);
const RetailerPosUsersPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/PosUserList/RetailerPosUsersPage'))
);
const RetailerDetailsPosUserPage = Loadable(
  lazy(
    () =>
      import('../features/Loyalty/partners/retailerPages/PosUserDetails/RetailerDetailsPosUserPage')
  )
);
//retailers
const RetailerProfilePage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/Profile/RetailerProfilePage'))
);
const CreateRetailerPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/Create/CreateRetailerPage'))
);
const DetailsRetailerPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/Details/DetailsRetailerPage'))
);
const UpdateRetailerPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/Update/UpdateRetailerPage'))
);
const DistributorProfilePage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/Profile/DistributerProfilePage'))
);
const DistributorDetailsBrandPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/partners/distributorPages/BrandDetails/DistributorDetailsBrandPage'
      )
  )
);
const BrandsPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/distributorPages/BrandsList/BrandsPage'))
);
const DistributerProductsPage = Loadable(
  lazy(
    () =>
      import('../features/Loyalty/partners/distributorPages/ProductList/DistributerProductsPage')
  )
);
//posUsers
const CreatePosUserPage = Loadable(
  lazy(() => import('../features/Loyalty/posUsers/pages/Create/CreatePosUserPage'))
);
//transactions pages
const TransactionsPage = Loadable(
  lazy(() => import('../features/Transactions/pages/Index/TransactionsPage'))
);
const RetailerTransactionsPage = Loadable(
  lazy(() => import('../features/Loyalty/partners/retailerPages/TransactionList/TransactionsPage'))
);
const RetailerTransactionsDetailsPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/partners/retailerPages/TransactionDetails/RetailerTransactionDetailsPage'
      )
  )
);
const DistributorTransactionsPage = Loadable(
  lazy(
    () => import('../features/Loyalty/partners/distributorPages/TransactionList/TransactionsPage')
  )
);
const DistributorTransactionsDetailsPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/partners/distributorPages/TransactionDetails/DistributorTransactionDetailsPage'
      )
  )
);
const TransactionDetails = Loadable(
  lazy(() => import('../features/Transactions/pages/Details/TransactionDetailsPage'))
);
const DetailsPosUserPage = Loadable(
  lazy(() => import('../features/Loyalty/posUsers/pages/Details/DetailsPosUserPage'))
);
const PosUsersByRetailerIdPage = Loadable(
  lazy(() => import('../features/Loyalty/posUsers/pages/Index/PosUsersPage'))
);
const CreateSimpleCashbackSpecificationPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/posUsers/components/CashbackSpecification/SimpleCashbackSpecification/CreateSimpleCashbackSpecificationPage'
      )
  )
);
const CreateCascadingCashbackSpecificationPage = Loadable(
  lazy(
    () =>
      import(
        '../features/Loyalty/posUsers/components/CashbackSpecification/CascadingCashbackSpecification/CreateCascadingCashbackSpecificationPage'
      )
  )
);
const CreateCustomTransactionPage = Loadable(
  lazy(
    () =>
      import('../features/Loyalty/posUsers/pages/CustomTransactions/CreateCustomTransactionPage')
  )
);
//product match
const ProductMatchPage = Loadable(
  lazy(() => import('../features/ProductEs/pages/Index/ProductMatchPage'))
);
const ProductNamesEntryPage = Loadable(
  lazy(() => import('../features/ProductEs/pages/NamesEntry/ProductNamesEntryPage'))
);
//contractors
const DetailsContractorPage = Loadable(
  lazy(() => import('../features/Contractor/pages/Details/DetailsContractorPage'))
);
const DetailsRiskContractorPage = Loadable(
  lazy(() => import('../features/Contractor/pages/Details/DetailsRiskContractorPage'))
);
const IndexContractorsInProcessPage = Loadable(
  lazy(() => import('../features/Contractor/pages/Index/IndexContractorsInProcessPage'))
);
const IndexRiskContractorPage = Loadable(
  lazy(() => import('../features/Contractor/pages/Index/IndexRiskContractorPage'))
);
//fiscalBills
const FiscalBillsPageWithPfrSearch = Loadable(
  lazy(() => import('../features/FiscalBills/pages/IndexByPfr/FiscalBillsPageWithPfrSearch'))
);
const FiscalBillDetailsPage = Loadable(
  lazy(() => import('../features/FiscalBills/pages/Details/FiscalBillDetailsPage'))
);
//fiscalItemClaims
const FiscalItemClaimsPage = Loadable(
  lazy(() => import('../features/FiscalItemClaims/pages/Index/FiscalItemClaimsPage'))
);
const FiscalItemClaimDetailsPage = Loadable(
  lazy(() => import('../features/FiscalItemClaims/pages/Details/FiscalItemClaimDetailsPage'))
);
//endUsers
const CreateEndUserPage = Loadable(
  lazy(() => import('../features/Loyalty/users/components/EndUser/CreateEndUserPage'))
);
const DetailsEndUserPage = Loadable(
  lazy(() => import('../features/EndUsers/pages/Details/EndUserDetailsPage'))
);
const EndUsersPage = Loadable(lazy(() => import('../features/EndUsers/pages/Index/EndUsersPage')));
const TransactionDetailsPage = Loadable(
  lazy(() => import('../features/EndUsers/pages/TransactionDetails/TransactionDetailsPage'))
);
const FiscalBillDetailsPageForEndUsers = Loadable(
  lazy(() => import('../features/EndUsers/pages/FiscalBillDetails/FiscalBillDetailsPage'))
);
//adminUsers
const AdminUsersPage = Loadable(
  lazy(() => import('../features/AdminUsers/pages/Index/AdminUsersPage'))
);
const DetailsAdminUserPage = Loadable(
  lazy(() => import('../features/AdminUsers/pages/Details/AdminUserDetailsPage'))
);
//productApprovalOperaterUsers
const CreateProductApprovalOperaterUserPage = Loadable(
  lazy(
    () =>
      import(
        '../features/ProductApprovalOperaterUser/pages/Create/CreateProductApprovalOperaterUserPage'
      )
  )
);
//specifications
const SpecificationIndexPage = Loadable(
  lazy(() => import('../features/Specification/pages/Details/DetailsSpecificationPage'))
);
const SpecificationAdminPage = Loadable(
  lazy(() => import('../features/Specification/pages/AdminDetails/AdminDetailsSpecificationPage'))
);
//contentEditorUsers
const CreateContentEditorUserPage = Loadable(
  lazy(() => import('../features/ContentEditorUser/pages/Create/CreateContentEditorUserPage'))
);
//underwriterUsers
const CreateUnderwriterUserPage = Loadable(
  lazy(() => import('../features/UnderwriterUser/pages/Create/CreateUnderwriterUserPage'))
);
//platformAdminUsers
const CreatePlatformAdminUserPage = Loadable(
  lazy(() => import('../features/PlatformAdminUser/pages/Create/CreatePlatformAdminUserPage'))
);
//insuranceAdminUsers
const CreateInsuranceAdminUserPage = Loadable(
  lazy(() => import('../features/InsuranceAdminUser/pages/Create/CreateInsuranceAdminUserPage'))
);
//insuranceAgentUsers
const CreateInsuranceAgentUserPage = Loadable(
  lazy(() => import('../features/InsuranceAgentUser/pages/Create/CreateInsuranceAgentUserPage'))
);
const InternalAgentsPage = Loadable(
  lazy(() => import('../features/AdminUsers/pages/AgentIndex/InternalAgentsPage'))
);
const ExternalAgentsPage = Loadable(
  lazy(() => import('../features/AdminUsers/pages/AgentIndex/ExternalAgentsPage'))
);
//partnerUsers
const CreatePartnerUserPage = Loadable(
  lazy(() => import('../features/Loyalty/users/components/PartnerUser/CreatePartnerUserPage'))
);
const DetailsPartnerUserPage = Loadable(
  lazy(() => import('../features/Loyalty/users/components/PartnerUser/DetailsPartnerUserPage'))
);
const PartnerUsersByPartnerIdPage = Loadable(
  lazy(() => import('../features/Loyalty/users/components/PartnerUser/PartnerUsersByPartnerIdPage'))
);
//pages
const PagesPage = Loadable(lazy(() => import('../features/CMS/pages/pages/Index/PagesPage')));
const CreatePagePage = Loadable(
  lazy(() => import('../features/CMS/pages/pages/Create/CreatePagePage'))
);
const UpdatePagePage = Loadable(
  lazy(() => import('../features/CMS/pages/pages/Update/UpdatePagePage'))
);
const DetailsPagePage = Loadable(
  lazy(() => import('../features/CMS/pages/pages/Details/DetailsPagePage'))
);
//pages
const BannersPage = Loadable(
  lazy(() => import('../features/CMS/banners/pages/Index/components/BannersPage'))
);
const CreateBannerPage = Loadable(
  lazy(() => import('../features/CMS/banners/pages/Create/CreateBannerPage'))
);
const UpdateBannerPage = Loadable(
  lazy(() => import('../features/CMS/banners/pages/Update/UpdateBannerPage'))
);
const DetailsBannerPage = Loadable(
  lazy(() => import('../features/CMS/banners/pages/Details/DetailsBannerPage'))
);
//codeBook
const CodeBookSettingsPage = Loadable(
  lazy(() => import('../features/Codebook/pages/Settings/CodeBookSettingsPage'))
);
//blackList
const BlackListPage = Loadable(
  lazy(() => import('../features/BlackList/pages/Index/BlackListPage'))
);
//whitelist
const ProductMatchesPage = Loadable(
  lazy(() => import('../features/ProductEs/pages/ProductMatches/ProductMatchesPage'))
);
//invoices
const InvoicesWithErrorsPage = Loadable(
  lazy(() => import('../features/Invoice/pages/Index/InvoicesWithErrorsPage'))
);
//reports
const DailyPaymentsPage = Loadable(
  lazy(() => import('../features/Reports/pages/DailyPaymentsIndex/DailyPaymentsPage'))
);
